import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Ticket, Smartphone, Plus, Edit, Trash2, Power, Coins } from 'lucide-react';

const EMPTY_VP = { name: '', code: '', description: '', instructions: '', logo_url: '', enabled: true };
const EMPTY_MOMO = {
  name: '', code: '', country: '', currency: '', description: '', logo_url: '',
  min_amount: 0, max_amount: 0, daily_limit: 0, enabled: true,
  required_fields: [
    { key: 'phone', label: 'Mobile Number', type: 'tel', required: true },
    { key: 'full_name', label: 'Full Name', type: 'text', required: true },
  ],
};
const EMPTY_CRYPTO = {
  currency: '', network: '', network_other: '', address: '', memo_tag: '',
  min_amount: 0, instructions: '', enabled: true,
};
const NETWORK_PRESETS = ["Bitcoin", "Ethereum", "Polygon", "BNB Smart Chain", "Tron",
  "Solana", "Arbitrum", "Optimism", "Base", "Monad", "HyperEVM"];

export const AdminDepositMethodsPage = () => {
  const [currencies, setCurrencies] = useState([]);
  const [voucherProviders, setVoucherProviders] = useState([]);
  const [momoProviders, setMomoProviders] = useState([]);
  const [cryptoWallets, setCryptoWallets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [vpForm, setVpForm] = useState(null);
  const [momoForm, setMomoForm] = useState(null);
  const [cryptoForm, setCryptoForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, v, m, cw] = await Promise.all([
        publicAPI.getCurrencies(true),
        adminAPI.listVoucherProviders(),
        adminAPI.listMomoProviders(),
        adminAPI.listCryptoDepositWallets(),
      ]);
      setCurrencies(c.data);
      setVoucherProviders(v.data);
      setMomoProviders(m.data);
      setCryptoWallets(cw.data);
    } catch (e) {
      toast.error('Failed to load providers');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // ---- Voucher Providers ----
  const saveVp = async () => {
    if (!vpForm.name || !vpForm.code) return toast.error('Name and code required');
    try {
      if (vpForm.id) {
        await adminAPI.updateVoucherProvider(vpForm.id, vpForm);
      } else {
        await adminAPI.createVoucherProvider(vpForm);
      }
      toast.success('Saved');
      setVpForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const toggleVp = async (p) => {
    try {
      await adminAPI.updateVoucherProvider(p.id, { enabled: !p.enabled });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const deleteVp = async (p) => {
    if (!window.confirm(`Delete ${p.name}? Users will no longer see this provider.`)) return;
    try {
      await adminAPI.deleteVoucherProvider(p.id);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  // ---- Mobile Money Providers ----
  const saveMomo = async () => {
    if (!momoForm.name) return toast.error('Name required');
    try {
      if (momoForm.id) {
        await adminAPI.updateMomoProvider(momoForm.id, momoForm);
      } else {
        await adminAPI.createMomoProvider(momoForm);
      }
      toast.success('Saved');
      setMomoForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const toggleMomo = async (p) => {
    try {
      await adminAPI.updateMomoProvider(p.id, { enabled: !p.enabled });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const deleteMomo = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      await adminAPI.deleteMomoProvider(p.id);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  // Manage required_fields inside momo form
  const addField = () => {
    setMomoForm({
      ...momoForm,
      required_fields: [...(momoForm.required_fields || []), { key: '', label: '', type: 'text', required: true }],
    });
  };
  const updateField = (i, patch) => {
    const list = [...(momoForm.required_fields || [])];
    list[i] = { ...list[i], ...patch };
    setMomoForm({ ...momoForm, required_fields: list });
  };
  const removeField = (i) => {
    const list = [...(momoForm.required_fields || [])];
    list.splice(i, 1);
    setMomoForm({ ...momoForm, required_fields: list });
  };

  // ---- Crypto Deposit Wallets ----
  const saveCrypto = async () => {
    const network = cryptoForm.network === 'Other' ? (cryptoForm.network_other || '').trim() : cryptoForm.network;
    if (!cryptoForm.currency || !network || !cryptoForm.address) {
      return toast.error('Currency, network and address are required');
    }
    const payload = {
      currency: cryptoForm.currency,
      network,
      address: cryptoForm.address.trim(),
      memo_tag: cryptoForm.memo_tag || '',
      min_amount: parseFloat(cryptoForm.min_amount) || 0,
      instructions: cryptoForm.instructions || '',
      enabled: cryptoForm.enabled,
    };
    try {
      if (cryptoForm.id) {
        await adminAPI.updateCryptoDepositWallet(cryptoForm.id, payload);
        toast.success('Wallet updated');
      } else {
        await adminAPI.createCryptoDepositWallet(payload);
        toast.success('Wallet added');
      }
      setCryptoForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed'); }
  };

  const toggleCrypto = async (w) => {
    try {
      await adminAPI.updateCryptoDepositWallet(w.id, { enabled: !w.enabled });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const deleteCrypto = async (w) => {
    if (!window.confirm(`Delete ${w.network} wallet for ${w.currency}?`)) return;
    try {
      await adminAPI.deleteCryptoDepositWallet(w.id);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const openCryptoEdit = (w) => {
    const isPreset = NETWORK_PRESETS.includes(w.network);
    setCryptoForm({
      ...w,
      network: isPreset ? w.network : 'Other',
      network_other: isPreset ? '' : w.network,
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-deposit-methods-page">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Deposit &amp; Withdrawal Methods</h1>
          <p className="mt-2 text-muted-foreground">Manage supported external voucher providers and mobile money channels</p>
        </div>

        <Tabs defaultValue="vouchers">
          <TabsList>
            <TabsTrigger value="vouchers" data-testid="tab-vouchers"><Ticket className="h-4 w-4 mr-2" /> Voucher Providers ({voucherProviders.length})</TabsTrigger>
            <TabsTrigger value="momo" data-testid="tab-momo"><Smartphone className="h-4 w-4 mr-2" /> Mobile Money ({momoProviders.length})</TabsTrigger>
            <TabsTrigger value="crypto" data-testid="tab-crypto"><Coins className="h-4 w-4 mr-2" /> Crypto Wallets ({cryptoWallets.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="vouchers">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle>External Voucher Providers</CardTitle>
                  <Button onClick={() => setVpForm({ ...EMPTY_VP })} data-testid="new-voucher-provider-btn"><Plus className="h-4 w-4 mr-2" /> New Provider</Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? <p className="text-center py-8 text-muted-foreground">Loading...</p> : voucherProviders.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No voucher providers yet. Add ones like OTT, 1Voucher, Blu, etc.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {voucherProviders.map(p => (
                        <TableRow key={p.id} data-testid={`vp-row-${p.id}`}>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell className="font-mono text-xs">{p.code}</TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{p.description}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={p.enabled ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' : 'bg-muted'}>
                              {p.enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="icon" variant="ghost" onClick={() => toggleVp(p)} title="Toggle" data-testid={`toggle-vp-${p.id}`}><Power className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => setVpForm({ ...p })} data-testid={`edit-vp-${p.id}`}><Edit className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => deleteVp(p)} data-testid={`delete-vp-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="momo">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle>Mobile Money Providers</CardTitle>
                  <Button onClick={() => setMomoForm({ ...EMPTY_MOMO })} data-testid="new-momo-provider-btn"><Plus className="h-4 w-4 mr-2" /> New Provider</Button>
                </div>
              </CardHeader>
              <CardContent>
                {momoProviders.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No mobile money providers yet. Add e.g. MTN MoMo, Vodacom M-Pesa, Airtel Money, etc.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Currency</TableHead>
                        <TableHead>Min</TableHead>
                        <TableHead>Max</TableHead>
                        <TableHead>Daily limit</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {momoProviders.map(p => (
                        <TableRow key={p.id} data-testid={`momo-row-${p.id}`}>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell>{p.country || '—'}</TableCell>
                          <TableCell>{p.currency || 'any'}</TableCell>
                          <TableCell>{p.min_amount || '—'}</TableCell>
                          <TableCell>{p.max_amount || '—'}</TableCell>
                          <TableCell>{p.daily_limit || '—'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={p.enabled ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' : 'bg-muted'}>
                              {p.enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="icon" variant="ghost" onClick={() => toggleMomo(p)} data-testid={`toggle-momo-${p.id}`}><Power className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => setMomoForm({ ...p })} data-testid={`edit-momo-${p.id}`}><Edit className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => deleteMomo(p)} data-testid={`delete-momo-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="crypto">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle>Crypto Deposit Wallets</CardTitle>
                  <Button onClick={() => setCryptoForm({ ...EMPTY_CRYPTO })} data-testid="new-crypto-wallet-btn"><Plus className="h-4 w-4 mr-2" /> New Wallet</Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Add wallet addresses per currency + network. Users choose one when depositing crypto and paste their transaction hash for your review.</p>
              </CardHeader>
              <CardContent>
                {cryptoWallets.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No crypto wallets configured yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Currency</TableHead>
                        <TableHead>Network</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Memo/Tag</TableHead>
                        <TableHead>Min</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cryptoWallets.map(w => (
                        <TableRow key={w.id} data-testid={`crypto-row-${w.id}`}>
                          <TableCell className="font-medium">{w.currency}</TableCell>
                          <TableCell>{w.network}</TableCell>
                          <TableCell className="font-mono text-xs max-w-[200px] truncate" title={w.address}>{w.address}</TableCell>
                          <TableCell className="font-mono text-xs">{w.memo_tag || '—'}</TableCell>
                          <TableCell>{w.min_amount || '—'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={w.enabled ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' : 'bg-muted'}>
                              {w.enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="icon" variant="ghost" onClick={() => toggleCrypto(w)} data-testid={`toggle-crypto-${w.id}`}><Power className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => openCryptoEdit(w)} data-testid={`edit-crypto-${w.id}`}><Edit className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => deleteCrypto(w)} data-testid={`delete-crypto-${w.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Voucher Provider dialog */}
      <Dialog open={!!vpForm} onOpenChange={(v) => !v && setVpForm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{vpForm?.id ? 'Edit Voucher Provider' : 'New Voucher Provider'}</DialogTitle>
            <DialogDescription>These providers appear to users when depositing via External Voucher.</DialogDescription>
          </DialogHeader>
          {vpForm && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={vpForm.name} onChange={(e) => setVpForm({ ...vpForm, name: e.target.value })} placeholder="OTT Voucher" data-testid="vp-name-input" /></div>
              <div><Label>Code</Label><Input value={vpForm.code} onChange={(e) => setVpForm({ ...vpForm, code: e.target.value.toUpperCase() })} placeholder="OTT" data-testid="vp-code-input" /></div>
              <div><Label>Description</Label><Textarea rows={2} value={vpForm.description} onChange={(e) => setVpForm({ ...vpForm, description: e.target.value })} /></div>
              <div><Label>Instructions to users (optional)</Label><Textarea rows={3} value={vpForm.instructions} onChange={(e) => setVpForm({ ...vpForm, instructions: e.target.value })} placeholder="Enter your 14-digit OTT code exactly as printed on the voucher slip." /></div>
              <div className="flex items-center gap-2"><Switch checked={vpForm.enabled} onCheckedChange={(v) => setVpForm({ ...vpForm, enabled: v })} /><Label>Enabled</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVpForm(null)}>Cancel</Button>
            <Button onClick={saveVp} data-testid="save-vp-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Money Provider dialog */}
      <Dialog open={!!momoForm} onOpenChange={(v) => !v && setMomoForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{momoForm?.id ? 'Edit Mobile Money Provider' : 'New Mobile Money Provider'}</DialogTitle>
            <DialogDescription>Users can withdraw to these providers. Set daily limits to control exposure.</DialogDescription>
          </DialogHeader>
          {momoForm && (
            <div className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <div><Label>Name</Label><Input value={momoForm.name} onChange={(e) => setMomoForm({ ...momoForm, name: e.target.value })} placeholder="MTN MoMo" data-testid="momo-name-input" /></div>
                <div><Label>Code</Label><Input value={momoForm.code || ''} onChange={(e) => setMomoForm({ ...momoForm, code: e.target.value.toUpperCase() })} placeholder="MTN" /></div>
                <div><Label>Country</Label><Input value={momoForm.country || ''} onChange={(e) => setMomoForm({ ...momoForm, country: e.target.value })} placeholder="South Africa" /></div>
                <div>
                  <Label>Currency (leave blank for any)</Label>
                  <Select value={momoForm.currency || 'any'} onValueChange={(v) => setMomoForm({ ...momoForm, currency: v === 'any' ? '' : v })}>
                    <SelectTrigger data-testid="momo-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any currency</SelectItem>
                      {currencies.map(c => <SelectItem key={c.symbol} value={c.symbol}>{c.symbol}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Min amount</Label><Input type="number" step="0.01" value={momoForm.min_amount} onChange={(e) => setMomoForm({ ...momoForm, min_amount: parseFloat(e.target.value) || 0 })} /></div>
                <div><Label>Max per transaction (0 = none)</Label><Input type="number" step="0.01" value={momoForm.max_amount} onChange={(e) => setMomoForm({ ...momoForm, max_amount: parseFloat(e.target.value) || 0 })} data-testid="momo-max-input" /></div>
                <div className="md:col-span-2"><Label>Daily limit per user (0 = none)</Label><Input type="number" step="0.01" value={momoForm.daily_limit} onChange={(e) => setMomoForm({ ...momoForm, daily_limit: parseFloat(e.target.value) || 0 })} data-testid="momo-daily-input" /></div>
                <div className="md:col-span-2"><Label>Description</Label><Textarea rows={2} value={momoForm.description} onChange={(e) => setMomoForm({ ...momoForm, description: e.target.value })} placeholder="Payouts within 1 hour on business days." /></div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Fields the user must fill</Label>
                  <Button size="sm" variant="outline" onClick={addField} data-testid="add-momo-field-btn"><Plus className="h-3 w-3 mr-1" /> Add field</Button>
                </div>
                <div className="space-y-2">
                  {(momoForm.required_fields || []).map((f, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`momo-field-row-${i}`}>
                      <Input placeholder="key (e.g. phone)" className="col-span-3" value={f.key} onChange={(e) => updateField(i, { key: e.target.value })} />
                      <Input placeholder="Label shown to user" className="col-span-4" value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} />
                      <Select value={f.type} onValueChange={(v) => updateField(i, { type: v })}>
                        <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="tel">Phone</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="col-span-2 flex items-center gap-1 text-xs">
                        <Switch checked={f.required} onCheckedChange={(v) => updateField(i, { required: v })} />
                        <span>Required</span>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => removeField(i)} className="col-span-1"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2"><Switch checked={momoForm.enabled} onCheckedChange={(v) => setMomoForm({ ...momoForm, enabled: v })} /><Label>Enabled</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMomoForm(null)}>Cancel</Button>
            <Button onClick={saveMomo} data-testid="save-momo-btn">Save Provider</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crypto Wallet dialog */}
      <Dialog open={!!cryptoForm} onOpenChange={(v) => !v && setCryptoForm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{cryptoForm?.id ? 'Edit Crypto Wallet' : 'New Crypto Deposit Wallet'}</DialogTitle>
            <DialogDescription>Users pick one of these wallets when depositing crypto. They will paste a tx hash for you to verify.</DialogDescription>
          </DialogHeader>
          {cryptoForm && (
            <div className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label>Currency to credit</Label>
                  <Select value={cryptoForm.currency} onValueChange={(v) => setCryptoForm({ ...cryptoForm, currency: v })}>
                    <SelectTrigger data-testid="crypto-currency-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {currencies.map(c => <SelectItem key={c.symbol} value={c.symbol}>{c.symbol} — {c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Network / Blockchain</Label>
                  <Select value={cryptoForm.network} onValueChange={(v) => setCryptoForm({ ...cryptoForm, network: v })}>
                    <SelectTrigger data-testid="crypto-network-select"><SelectValue placeholder="Pick a network" /></SelectTrigger>
                    <SelectContent>
                      {NETWORK_PRESETS.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                      <SelectItem value="Other">Other (specify below)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {cryptoForm.network === 'Other' && (
                <div>
                  <Label>Custom network name</Label>
                  <Input value={cryptoForm.network_other || ''} onChange={(e) => setCryptoForm({ ...cryptoForm, network_other: e.target.value })} placeholder="e.g. Sui, Aptos, TON…" data-testid="crypto-network-other-input" />
                </div>
              )}
              <div>
                <Label>Wallet Address</Label>
                <Input value={cryptoForm.address} onChange={(e) => setCryptoForm({ ...cryptoForm, address: e.target.value })} className="font-mono" placeholder="0x… / bc1… / Txxx…" data-testid="crypto-address-input" />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label>Memo / Destination Tag (optional)</Label>
                  <Input value={cryptoForm.memo_tag || ''} onChange={(e) => setCryptoForm({ ...cryptoForm, memo_tag: e.target.value })} placeholder="Used for XRP, XLM, BNB Beacon…" />
                </div>
                <div>
                  <Label>Min deposit (0 = none)</Label>
                  <Input type="number" step="0.00000001" value={cryptoForm.min_amount} onChange={(e) => setCryptoForm({ ...cryptoForm, min_amount: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Instructions to users (optional)</Label>
                <Textarea rows={3} value={cryptoForm.instructions || ''} onChange={(e) => setCryptoForm({ ...cryptoForm, instructions: e.target.value })} placeholder="Only send USDT on Polygon. Include memo. Deposits below 5 USDT are ignored." />
              </div>
              <div className="flex items-center gap-2"><Switch checked={cryptoForm.enabled} onCheckedChange={(v) => setCryptoForm({ ...cryptoForm, enabled: v })} /><Label>Enabled</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCryptoForm(null)}>Cancel</Button>
            <Button onClick={saveCrypto} data-testid="save-crypto-wallet-btn">Save Wallet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};
